import { CurrencyPipe, Location, NgFor, NgIf, PercentPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroupDirective, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router, RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleLeft, faCalendar, faCheck, faDollarSign, faEnvelope, faEye, faPaperPlane, faPencil, faPlus, faQuestionCircle, faSave, faSms, faTimes, faTrashCan, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { NgbDateParserFormatter, NgbDropdown, NgbDropdownButtonItem, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbInputDatepicker, NgbModal, NgbModalRef, NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { filter, lastValueFrom, Subject, Subscription, take, takeUntil } from 'rxjs';
import { AcceptProposalComponent } from '../accept-proposal/accept-proposal.component';
import { AlertService } from '../alert.service';
import { ClientService } from '../client.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ConversationService } from '../conversation.service';
import { SendSmsDialogComponent } from '../conversation/components/send-sms-dialog/send-sms-dialog.component';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { ProjectService } from '../project.service';
import { Proposal } from '../proposal';
import { ProposalBuilderComponent } from '../proposal-builder/proposal-builder.component';
import { ProposalExcessModalComponent } from '../proposal-excess-modal/proposal-excess-modal.component';
import { ProposalInteraction } from '../proposal-interaction';
import { ProposalInteractionPipe } from '../proposal-interaction.pipe';
import { ProposalMultiAgreementTypeOptionsModalComponent } from '../proposal-multi-agreement-type-options-modal/proposal-multi-agreement-type-options-modal.component';
import { ProposalPricing } from '../proposal-pricing';
import { ProposalRejectModalComponent } from '../proposal-reject-modal/proposal-reject-modal.component';
import { ProposalStatusPipe } from '../proposal-status.pipe';
import { ProposalTermsAndConditions, TermAndConditionsVersion } from '../shared/interfaces/settings/proposal-terms';
import { ProposalService } from '../proposal.service';
import { SendEmailComponent } from '../send-email/send-email.component';
import { SettingService } from '../setting.service';
import { LoaderService } from '../shared/services/loader.service';
import { Tax } from '../tax';
import { User } from '../user';

@Component({
  selector: 'app-proposal-detail',
  templateUrl: './proposal-detail.component.html',
  styleUrls: ['./proposal-detail.component.css'],
  imports: [NgbTooltip, FaIconComponent, NgIf, RouterLink, NgbPopover, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, ReactiveFormsModule, NgFor, NgbInputDatepicker, ProposalBuilderComponent, NgxSpinnerComponent, PercentPipe, CurrencyPipe, FormatDateTimePipe, ProposalStatusPipe, ProposalInteractionPipe]
})
export class ProposalDetailComponent implements OnInit, OnDestroy {
  req$ = new Subscription();
  // Modals
  private modalReference: NgbModalRef;
  @ViewChild('proposalBuilder') private proposalBuilder: ProposalBuilderComponent;
  @ViewChild('ALL_PROPOSALS_REJECTED_MODAL') private allProposalsRejectedModal: NgbModalRef;
  @ViewChild('invoiceForm') invoiceForm: FormGroupDirective;

  // Proposal
  proposalId: string;
  proposal: Proposal;
  proposalForm: UntypedFormGroup;
  firstInvoiceForm: UntypedFormGroup;
  taxes: Tax[] = [];
  proposalTerms: ProposalTermsAndConditions[] = [];
  termAndConditionsVersionList: TermAndConditionsVersion[] = [];
  proposalPricing: ProposalPricing;
  proposalInteractions: ProposalInteraction[] = [];
  showAdditionalInteractionData: boolean = false;
  proposalBuilderSubscription!: Subscription;
  amountType: string = 'PERCENT';
  isAccepted: boolean = false;
  invoiceRequired: boolean = true;
  acceptedProposal: any;

  // Multi Options
  multiOptionKeys: any[] = [];
  selectedMultiOptionKey: string;
  selectedMultiOptionPricing: any;

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faSave = faSave;
  faEye = faEye;
  faEnvelope = faEnvelope;
  faTrashCan = faTrashCan;
  faTimes = faTimes;
  faCheck = faCheck;
  faDollarSign = faDollarSign;
  faCalendar = faCalendar;
  faPlus = faPlus;
  faPencil = faPencil;
  faSms = faSms;
  faQuestionCircle = faQuestionCircle;
  faTriangleExclamation = faTriangleExclamation;
  faPaperPlane = faPaperPlane;
  twilioClient: any;

  componentDestroyed$: Subject<boolean> = new Subject()
  currentUserNumber = '+14782177074';
  loginDetails: User;
  selectPrimaryContact: any;

  private routerEventsSub?: Subscription;

  constructor(private router: Router,
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private proposalService: ProposalService,
    private clientService: ClientService,
    private alertService: AlertService,
    private settingService: SettingService,
    private conversationSerivce: ConversationService,
    private modalService: NgbModal,
    public location: Location,
    private dateParserFormatter: NgbDateParserFormatter,
    private loaderService: LoaderService) {
      this.req$.add(
      this.route.paramMap.subscribe((res: any) => {
        this.proposalId = res.params.proposalId;
      })
    );

  }

  ngOnInit(): void {
    this.loginDetails = JSON.parse(localStorage.getItem('login'));
    this.proposalForm = new UntypedFormGroup({
      name: new UntypedFormControl(),
      tax: new UntypedFormControl(),
      discountType: new UntypedFormControl('PERCENT'),
      discount: new UntypedFormControl(0),
      agreementType: new UntypedFormControl('SINGLE'),
      term: new UntypedFormControl(),
      version: new UntypedFormControl(),
      expirationDate: new UntypedFormControl(),
      taxMultiplierName: new UntypedFormControl()
    });
    this.proposalForm.controls.tax.disable();
    this.firstInvoiceForm = new UntypedFormGroup({
      amount: new UntypedFormControl(),
      amountType: new UntypedFormControl(),
      daysUntilDue: new UntypedFormControl(0)
    });

    this.firstLoadData();

    this.conversationSerivce.twilioClient
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((data) => {
        this.twilioClient = data;
      });
  }

  async firstLoadData(){
    await this.reloadData();
    await this.getProposalTermAndTaxes();
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    this.req$.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Delete Proposal Confirmation Modal
  openDeleteProposalConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this proposal?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed
      .pipe(take(1))
      .subscribe(() => {
        this.deleteProposal();
      });
  }

  // Open Send Email Modal
  openSendEmailModal(): void {
    if (this.firstInvoiceForm.valid || this.firstInvoiceForm.disabled) {
      const modalRef = this.modalService.open(SendEmailComponent);
      modalRef.componentInstance.clientId = this.proposal.clientId;
      modalRef.componentInstance.proposalId = this.proposal.id;
      modalRef.componentInstance.type = 'PROPOSAL';
      modalRef.componentInstance.subject = `${this.proposal.projectName} Proposal`;
      modalRef.componentInstance.showAttachments = true;
    } else {
      this.alertService.showWarningAlert('First Invoice amount must not be equal to zero');
      this.firstInvoiceForm.markAllAsTouched();
    }

  }

  openSendSmsModal(): void {
    if (this.firstInvoiceForm.valid || this.firstInvoiceForm.disabled) {
      this.loaderService.showSpinner();
      this.twilioClient.getUser(this.currentUserNumber).then(res => {
        if (res) {
          this.loaderService.hideSpinner();
          const modalRef = this.modalService.open(SendSmsDialogComponent);
          modalRef.componentInstance.clientId = this.proposal.clientId;
          modalRef.componentInstance.proposalId = this.proposal.id;
          modalRef.componentInstance.contact = this.proposal.contacts;
          modalRef.componentInstance.clientName = this.proposal.clientName;
          modalRef.componentInstance.twilioClient = this.twilioClient;
          modalRef.componentInstance.defaultMessage = 'You have a new Christmas Light Proposal from <OrgName>! Click here to see your proposal now! <LINK>';
          modalRef.componentInstance.sent
            .pipe(take(1))
            .subscribe((res) => {
              this.alertService.showSuccessAlert('Message sent');
            });
        }
      }).catch(_err => {
        this.loaderService.hideSpinner();
        this.alertService.showWarningAlert('User not found in Twilio');
      });
    } else {
      this.alertService.showWarningAlert('First Invoice amount must not be equal to zero');
      this.firstInvoiceForm.markAllAsTouched();
    }
  }

  // Open Accept Proposal Modal
  openAcceptProposalModal(): void {
    if (this.firstInvoiceForm.valid || this.firstInvoiceForm.disabled) {
      this.updateProposal();

      const firstInvoiceAmount = this.proposal.amountType == 'DOLLAR' ? this.proposal.amount : (this.proposal.amount / 100) * this.proposal.initialAcceptedPrice;

      const modalRef = this.modalService.open(AcceptProposalComponent);
      modalRef.componentInstance.proposal = this.proposal;
      modalRef.componentInstance.firstInvoice = {
        ...this.firstInvoiceForm.value
      };
      modalRef.componentInstance.saved
        .pipe(take(1))
        .subscribe((res: any) => {
          this.reloadData();

          if (firstInvoiceAmount > this.proposalPricing.total && res.invoiceNumber) {
            this.openInvoiceGreaterThanProposalAmount(res.invoiceNumber, res.invoiceId);
          }

        });
    } else {
      this.alertService.showWarningAlert('First Invoice amount must not be equal to zero');
      this.firstInvoiceForm.markAllAsTouched();
    }
  }

  openInvoiceGreaterThanProposalAmount(invoiceNumber: number, invoiceId: string) {
    const modalRef = this.modalService.open(ProposalExcessModalComponent);
    modalRef.componentInstance.invoiceNumber = invoiceNumber;
    modalRef.componentInstance.choice
      .pipe(take(1))
      .subscribe((action) => {
        if (action == 1) {
          this.router.navigateByUrl(`/invoices/${invoiceId}`, { replaceUrl: true });
        }
      });
  }

  // Open Proposal Multi Agreement Type Options Modal
  openProposalMultiAgreementTypeOptionsModal(): void {
    const modalRef = this.modalService.open(ProposalMultiAgreementTypeOptionsModalComponent);
    modalRef.componentInstance.proposalId = this.proposal.id;
    modalRef.componentInstance.multiOptions = this.proposal.multiOptions;
    modalRef.componentInstance.multiOptionsUpdated
      .pipe(take(1))
      .subscribe(() => {
        this.reloadData();
      });
  }

  // Multi Option Is Enabled
  multiOptionIsEnabled(key: string): boolean {
    return this.proposal.multiOptions[key].isEnabled;
  }

  private async reloadData(): Promise<void> {
    //Get scroll position
    const scrollContainer = document.querySelector('.content-container') as HTMLElement;
    const scrollY = scrollContainer ? scrollContainer.scrollTop : 0;

    this.loaderService.showSpinner();
    this.proposal = await lastValueFrom(this.proposalService.getProposal(this.proposalId));
    if (this.proposalBuilder) {
      this.proposalBuilder._proposal = this.proposal;
      await this.proposalBuilder.getFeaturesAndFeatureItems();
    }
    this.proposalPricing = await lastValueFrom(this.proposalService.calculateProposalPricing(this.proposalId));
    this.proposalInteractions = await lastValueFrom(this.proposalService.getProposalInteractions(this.proposalId));


    // Multi Options
    if (this.proposal.agreementType == 'MULTI') {
      this.multiOptionKeys = Object.keys(this.proposal.multiOptions);
      this.selectedMultiOptionKey = this.multiOptionKeys[0];
    }
    this.selectPrimaryContact = this.proposal.contacts.filter(contact => contact.isPrimary)[0];

    this.setProposalDetailFormValues();

    if(this.isAccepted){
      this.getAcceptedProposal();
    }

    setTimeout(() => {
      if (scrollContainer) scrollContainer.scrollTop = scrollY;
      setTimeout(() => {
        this.loaderService.hideSpinner();
      }, 200);
    }, 0);

    this.routerEventsSub?.unsubscribe();
    this.routerEventsSub = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger === 'popstate') {
          history.replaceState(null, null, `/projects/${this.proposal.projectId}`);
        }
      });
    this.req$.add(this.routerEventsSub);
  }


  termChange(event): void {
    this.selectTermsAndConditionsVersion(this.proposalForm.controls['term'].value, null);
    setTimeout(() => {
      this.updateProposal();
    }, 200)

  }

  selectTermsAndConditionsVersion(id: any, version: any): void {

    const term = this.proposalTerms?.find(t => String(t?.id) === String(id));
    const allVersions = Array.isArray(term?.versions) ? term!.versions.slice() : [];
    if (allVersions.length === 0) {
      this.termAndConditionsVersionList = [];
      this.proposalForm.get('version')?.setValue(null);
      return;
    }

    const norm = (n: any) => (n == null || n === '' ? null : Number(n));
    allVersions.forEach(v => (v.versionNumber = norm(v.versionNumber)));
    allVersions.sort((a, b) => (a.versionNumber ?? 0) - (b.versionNumber ?? 0));

    const active = allVersions.filter(v => v?.isArchived === false);

    const passedVersion = norm(version);
    const passedExists = passedVersion != null &&
      allVersions.some(v => v.versionNumber === passedVersion);

    const defaultActive = active.find(v => v?.isDefault === true);
    const highestActive = active.length ? active[active.length - 1] : null;

    let fallback: number | null =
      defaultActive?.versionNumber ??
      highestActive?.versionNumber ??
      (passedExists ? passedVersion : null);

    this.termAndConditionsVersionList =
      active.length > 0
        ? active
        : (passedExists
            ? allVersions.filter(v => v.versionNumber === passedVersion)
            : []);

    const control = this.proposalForm.get('version');
    if (passedExists) {
      control?.setValue(passedVersion);
    } else {
      control?.setValue(fallback);
    }
  }

  // Set Proposal Detail Form Values
  private setProposalDetailFormValues(): void {
    this.proposalForm.controls.name.setValue(this.proposal.name);
    this.proposalForm.controls.tax.setValue((this.proposal.taxMultiplier * 100).toFixed(3));
    this.proposalForm.controls.taxMultiplierName.setValue(this.proposal.taxMultiplierName);
    this.proposalForm.controls.discountType.setValue(this.proposal.discountType);
    this.proposalForm.controls.discount.setValue(this.proposal.discount);
    this.proposalForm.controls.agreementType.setValue(this.proposal.agreementType);
    this.proposalForm.controls.term.setValue(this.proposal.termId);
    this.proposalForm.controls.version.setValue(this.proposal.termVersion);
    this.proposalForm.controls.expirationDate.setValue(this.dateParserFormatter.parse(this.proposal.expirationDate));
    this.isAccepted = this.proposal.status == 'ACCEPTED' || this.proposal.status == 'MODIFIED';
    this.invoiceRequired = this.proposal.isInvoiceRequired
    this.firstInvoiceForm.controls.amount.setValue(this.proposal.amount);
    this.firstInvoiceForm.controls.daysUntilDue.setValue(this.proposal.daysUntilDue || 0);
    this.amountType = this.proposal.amountType ? this.proposal.amountType : this.amountType;
    this.firstInvoiceForm.controls.amountType.setValue(this.amountType);


    if (this.isAccepted) {
      this.proposalForm.disable();
      this.firstInvoiceForm.disable();
      this.proposalBuilder.featureForms.disable();
      this.proposalBuilder.featureItemForm.disable();
      this.proposalBuilder.newFeatureForm.disable();
      this.proposalBuilder.isAccepted = true;
    } else {
      this.proposalForm.enable();
      this.proposalForm.controls.tax.disable();
      this.firstInvoiceForm.enable();
      this.proposalBuilder.featureForms.enable();
      this.proposalBuilder.featureItemForm.enable();
      this.proposalBuilder.newFeatureForm.enable();
      this.proposalBuilder.isAccepted = false;
    }

    if (this.proposal.status == 'PENDING' && this.proposal.rowNumber > 1) {
      this.firstInvoiceForm.disable();
      this.proposalBuilder.featureForms.enable();
      this.proposalBuilder.featureItemForm.enable();
      this.proposalBuilder.newFeatureForm.enable();
      this.proposalBuilder.isAccepted = false;
    }

    if (this.proposal?.contacts?.length <= 0)  {
       this.firstInvoiceForm.controls.amount.setValidators(null);
      this.firstInvoiceForm.disable();
      this.firstInvoiceForm.updateValueAndValidity();
    } else {
      this.firstInvoiceForm.controls.amount.setValidators([Validators.required, Validators.min(1)]);
      this.firstInvoiceForm.enable()
      this.firstInvoiceForm.updateValueAndValidity();
    }

    if (this.invoiceRequired && !this.isAccepted) {
      this.firstInvoiceForm.get('amount').enable();
      this.firstInvoiceForm.get('daysUntilDue').enable();
      this.firstInvoiceForm.controls.amount.setValidators([Validators.required, Validators.min(1)]);
      this.firstInvoiceForm.controls.daysUntilDue.setValidators([Validators.required, Validators.min(0)]);
      this.firstInvoiceForm.updateValueAndValidity();
    } else {
      this.firstInvoiceForm.controls.amount.setValidators(null);
      this.firstInvoiceForm.get('amount').disable();
      this.firstInvoiceForm.get('daysUntilDue').disable();
      this.firstInvoiceForm.updateValueAndValidity();
    }
  }

  updateInvoiceRequired(event) {
    this.invoiceRequired = event.target.checked;

    if (this.invoiceRequired) {
      this.firstInvoiceForm.get('amount').enable();
      this.firstInvoiceForm.get('daysUntilDue').enable();
      this.firstInvoiceForm.controls.amount.setValidators([Validators.required, Validators.min(1)]);
      this.firstInvoiceForm.controls.daysUntilDue.setValidators([Validators.required, Validators.min(0)]);
      this.firstInvoiceForm.updateValueAndValidity();
    } else {
      this.firstInvoiceForm.controls.amount.setValidators(null);
      this.firstInvoiceForm.get('amount').disable();
      this.firstInvoiceForm.get('daysUntilDue').disable();
      this.firstInvoiceForm.updateValueAndValidity();
    }
    this.updateProposal();
  }

  // Update Proposal Agreement Type
  updateProposalAgreementType(): void {
    const agreementType = this.proposalForm.getRawValue().agreementType || this.proposalForm.value.agreementType;
    this.loaderService.showSpinner();
    this.proposalService.updateProposalAgreementType(this.proposalId, agreementType).subscribe({
      next: async () => {
        this.alertService.showSuccessAlert('Proposal Updated');
        this.reloadData();
        this.loaderService.hideSpinner(1000);
      },
      error: () => {
        this.loaderService.hideSpinner(1000);
      }
    });
  }

  // Update Proposal
  updateProposal(): void {
    if (this.proposalForm.valid) {
      this.loaderService.showSpinner();
      const proposal = {
        ...this.firstInvoiceForm.value,
        id: this.proposalId,
        name: this.proposalForm.value.name,
        taxPercent: this.proposalForm.get('tax').value,
        discountType: this.proposalForm.value.discountType,
        discount: this.proposalForm.value.discount,
        agreementType: this.proposalForm.getRawValue().agreementType || this.proposalForm.value.agreementType,
        termId: this.proposalForm.value.term,
        termVersion: this.proposalForm.value.version,
        taxMultiplierName: this.proposalForm.value.taxMultiplierName,
        expirationDate: this.dateParserFormatter.format(this.proposalForm.value.expirationDate),
        amountType: this.amountType,
        isInvoiceRequired: this.invoiceRequired
      };
      this.proposalService.updateProposal(proposal).subscribe({
        next: async () => {
          this.alertService.showSuccessAlert('Proposal Updated');
          this.reloadData();
          this.loaderService.hideSpinner(1000);
        },
        error: () => {
          this.loaderService.hideSpinner(1000);
        }
      });
    } else {
      this.proposalForm.markAllAsTouched();
    }
  }

  // Modify Proposal
  private async modifyProposal(): Promise<void> {
    const data: any = {
      proposalId: this.proposal.id,
      projectId: this.proposal.projectId,
      name: this.proposal.name,
      taxPercent: (this.proposal.taxMultiplier * 100).toFixed(3),
      taxMultiplierName: this.proposal.taxMultiplierName
    };
    this.proposalService.modifyProposal(data).subscribe((proposalId) => {
      this.router.navigate([`/proposals/${proposalId}`]).then(() => window.location.reload());
    });
  }

  confirmModifyProposal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to modify the proposal?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Continue";
    confirmationModalRef.componentInstance.confirmed
    .pipe(take(1))
    .subscribe(() => {
      this.modifyProposal();
    });
  }

  // Delete Proposal
  deleteProposal(): void {
    this.proposalService.deleteProposal(this.proposalId, this.proposal.projectId).subscribe(() => {
      this.router.navigateByUrl(`/projects/${this.proposal.projectId}`, { replaceUrl: true });
    });
  }

  // View Proposal
  viewProposal(): void {
    if (this.firstInvoiceForm.valid || this.firstInvoiceForm.disabled) {
      this.updateProposal();
      const url = this.router.serializeUrl(this.router.createUrlTree([`/proposals/${this.proposalId}/preview`]));
      window.open(url);
    } else {
      this.alertService.showWarningAlert('First Invoice amount must not be equal to zero');
      this.firstInvoiceForm.markAllAsTouched()
    }
  }

  // Open All Proposals Rejected Modal
  openAllProposalsRejectedModal(): void {
    const rejectModal = this.modalService.open(ProposalRejectModalComponent);
    rejectModal.componentInstance.clientId = this.proposal.clientId;
    rejectModal.componentInstance.saved
      .pipe(take(1))
      .subscribe((res: any) => {
        this.applyAllProposalsRejectedUpdates(res.updateProjectStatus, res.updateClientStatus);
      });
  }

  // Select Tax
  selectTax(tax: Tax): void {
    this.proposalForm.controls.tax.setValue((tax.multiplier * 100).toFixed(3));
    this.proposalForm.controls.taxMultiplierName.setValue(tax.name);
    this.updateProposal();
  }

  // Get Proposals For Project
  async getProposalsForProject(): Promise<Proposal[]> {
    const params = {
      searchTerm: null,
      sortBy: 'proposal_name',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      'filter:prop.project_id': JSON.stringify([this.proposal.projectId])
    };
    const res = await lastValueFrom(this.proposalService.getProposals(params));
    return res.proposals;
  }

  // Apply All Proposals Rejected Updates
  async applyAllProposalsRejectedUpdates(updateProjectStatus: boolean, updateClientStatus: boolean): Promise<void> {
    // if (this.proposalRejectedModalForm.valid) {
    let anyUpdated = false;
    if (updateProjectStatus) {
      anyUpdated = true;
      await lastValueFrom(this.projectService.updateProjectStatus(this.proposal.projectId, 'LOST'));
    }
    if (updateClientStatus) {
      anyUpdated = true;
      await lastValueFrom(this.clientService.updateClientStatus(this.proposal.clientId, 'INACTIVE'));
    }
    this.proposalService.rejectProposal(this.proposalId, this.proposal.projectId, false)
      .pipe().subscribe(res => {
        if (res) this.alertService.showSuccessAlert('Proposal Rejected');
        this.reloadData();
      });
  }

  // Toggle Show Additional Interaction Data
  toggleShowAdditionInteractionData(): void {
    this.showAdditionalInteractionData = !this.showAdditionalInteractionData;
  }

  async getProposalTermAndTaxes(){
    this.taxes = await lastValueFrom(this.settingService.getTaxes());
    this.proposalTerms = await lastValueFrom(this.settingService.getProposalTermsAndConditions());
    this.proposalTerms = this.proposalTerms.filter(term => term.isArchived == false);
    this.selectTermsAndConditionsVersion(this.proposalForm.controls.term.value, this.proposalForm.controls.version.value);
  }

  private getAcceptedProposal(): void {
    this.proposalService.getAcceptedProposalForProject(this.proposal.projectId).subscribe((acceptedProposal) => {
      this.acceptedProposal = acceptedProposal;
    });
  }


  // Proposal Form Accessors
  get name() { return this.proposalForm.controls.name; }
  get tax() { return this.proposalForm.controls.tax; }
  get discount() { return this.proposalForm.controls.discount; }
  get expirationDate() { return this.proposalForm.controls.expirationDate; }
  get amount() { return this.firstInvoiceForm.controls.amount; }
  get daysUntilDue() { return this.firstInvoiceForm.controls.daysUntilDue; }

}
